import { env } from '../../config/env'
import { expireAuthSession, getApiSession } from '../../auth/session'
import { AbortedError, ApiError, ConflictError } from '../errors'
import { captureApiError } from '../../instrument'

// Cliente HTTP tipado. Base por ambiente (env): 'v1' = monolito (api/…),
export type ApiBase = 'v1' | 'v2'

const baseUrl = (base: ApiBase): string => (base === 'v2' ? env.apiBaseV2 : env.apiBase)

function buildQuery(q?: Record<string, unknown>): string {
  if (!q) {
    return ''
  }
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== null && v !== '') {
      if (Array.isArray(v)) {
        for (const item of v) {p.append(k, String(item))}
      } else {p.append(k, String(v))}
    }
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

async function request<T>(method: string, base: ApiBase, path: string, opts: { query?: Record<string, unknown>; body?: unknown; extraHeaders?: Record<string, string>; signal?: AbortSignal } = {}): Promise<T> {
  const url = `${baseUrl(base)}/${path.replace(/^\/+/, '')}${buildQuery(opts.query)}`
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.extraHeaders }
  const multipart = opts.body instanceof FormData
  if (opts.body !== undefined && !multipart) {
    headers['Content-Type'] = 'application/json'
  }
  const token = getApiSession().token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let res: Response
  try {
    res = await fetch(url, { method, headers, body: multipart ? opts.body as FormData : opts.body !== undefined ? JSON.stringify(opts.body) : undefined, signal: opts.signal })
  } catch (err) {
    // AbortController.abort() (ex.: reload() cancelando uma chamada obsoleta) não é uma
    // falha de rede — o caller deve tratar silenciosamente, sem toast de erro.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new AbortedError(`Requisição a ${url} abortada.`)
    }
    const error = new ApiError(0, 'NETWORK', `Falha de rede ao acessar ${url}. (Backend offline ou CORS — ver proxy do Vite.)`)
    captureApiError(error, { url, method, base })
    throw error
  }

  if (res.status === 409 || res.status === 400) {
    let code: string | null = null
    let message: string | null = null
    try {
      const body = (await res.clone().json()) as { code?: string; message?: string; error?: string }
      code = body?.code ?? null
      message = body?.message ?? body?.error ?? null
    } catch {
      /* ignore */
    }
    if (code === 'JOURNEY_STEP_NOT_EDITABLE') {
      const error = res.status === 409
        ? new ConflictError('Esta etapa já foi movimentada e não pode ser alterada.')
        : new ApiError(400, code, 'Esta etapa já foi movimentada e não pode ser alterada.')
      captureApiError(error, { status: res.status, code, path, method, base })
      throw error
    }
    if (res.status === 400) {
      const error = new ApiError(400, code, message ?? 'HTTP 400')
      captureApiError(error, { status: 400, code: code ?? undefined, path, method, base })
      throw error
    }
    const error = new ConflictError(code === 'JOURNEY_STEP_CONFLICT'
      ? 'Conflito de nível: outro operador iniciou esta etapa.'
      : message ?? 'Conflito ao executar a ação. Lista atualizada.')
    captureApiError(error, { status: 409, code: code ?? undefined, path, method, base })
    throw error
  }
  if (res.status === 401) {
    expireAuthSession(token)
    const error = new ApiError(401, 'UNAUTHORIZED', 'Sessão expirada ou token ausente.')
    captureApiError(error, { status: 401, path, method, base })
    throw error
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = (await res.clone().json()) as Record<string, unknown>
      if (j) {
        const strMsg = typeof j.message === 'string' ? j.message : (typeof j.error === 'string' ? j.error : null)
        msg = strMsg || (typeof j === 'object' ? JSON.stringify(j) : String(j))
      }
    } catch {
      /* ignore */
    }
    const error = new ApiError(res.status, null, msg)
    captureApiError(error, { status: res.status, path, method, base })
    throw error
  }
  if (res.status === 204) {
    return undefined as T
  }
  try {
    return (await res.json()) as T
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new AbortedError(`Requisição a ${url} abortada.`)
    }
    throw err
  }
}

export interface RequestOptions { signal?: AbortSignal }

export const http = {
  get: <T>(base: ApiBase, path: string, query?: Record<string, unknown>, opts?: RequestOptions) => request<T>('GET', base, path, { query, signal: opts?.signal }),
  post: <T>(base: ApiBase, path: string, body?: unknown, extraHeaders?: Record<string, string>, opts?: RequestOptions) => request<T>('POST', base, path, { body, extraHeaders, signal: opts?.signal }),
  patch: <T>(base: ApiBase, path: string, body?: unknown, extraHeaders?: Record<string, string>, opts?: RequestOptions) => request<T>('PATCH', base, path, { body, extraHeaders, signal: opts?.signal }),
  put: <T>(base: ApiBase, path: string, body?: unknown, extraHeaders?: Record<string, string>, opts?: RequestOptions) => request<T>('PUT', base, path, { body, extraHeaders, signal: opts?.signal }),
  delete: <T>(base: ApiBase, path: string, extraHeaders?: Record<string, string>, opts?: RequestOptions) => request<T>('DELETE', base, path, { extraHeaders, signal: opts?.signal }),
}

// Helpers de rota (providerId/locationId vêm da sessão em runtime; env é fallback dev).
export const pid = () => getApiSession().providerId ?? ''
export const lid = () => getApiSession().locationId ?? ''
/** rota do ticketing v2: ticketing/providers/{pid}/… */
export const ticketing = (suffix: string) => `ticketing/providers/${pid()}/${suffix.replace(/^\/+/, '')}`
/** rota do monolito v1: api/providers/{pid}/… */
export const apiV1 = (suffix: string) => `api/providers/${pid()}/${suffix.replace(/^\/+/, '')}`
